import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const RegisterPage = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: ""
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password.length < 6) {
            toast.error("Password minimal 6 karakter");
            return;
        }

        setLoading(true);

        try {
            await register(formData.name, formData.email, formData.password);
            toast.success("Registrasi berhasil!");
            navigate("/dashboard");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Registrasi gagal. Coba lagi.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange-500 to-yellow-500 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1587038787166-becd08a156f7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                        alt="Team Working"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="relative z-10 flex flex-col justify-center p-12 text-white">
                    <Link to="/" className="flex items-center gap-2 mb-12">
                        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl">mangoplus.ai</span>
                    </Link>
                    <h1 className="font-heading text-4xl font-bold mb-4">
                        Bergabung Sekarang
                    </h1>
                    <p className="text-xl text-white/80 max-w-md">
                        Daftar gratis dan mulai buat foto produk profesional dengan teknologi AI terdepan.
                    </p>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-heading font-bold text-xl">mangoplus.ai</span>
                        </Link>
                    </div>

                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">Daftar</h2>
                    <p className="text-gray-600 mb-8">
                        Sudah punya akun?{" "}
                        <Link to="/login" className="text-red-500 hover:text-red-600 font-medium">
                            Masuk
                        </Link>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nama Lengkap</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="John Doe"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                className="h-12 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-red-500"
                                data-testid="register-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="nama@email.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="h-12 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-red-500"
                                data-testid="register-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Minimal 6 karakter"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    minLength={6}
                                    className="h-12 bg-gray-50 border-transparent focus:bg-white focus:ring-2 focus:ring-red-500 pr-12"
                                    data-testid="register-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-lg"
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading ? "Memproses..." : "Daftar Sekarang"}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-gray-500 text-center">
                        Dengan mendaftar, Anda menyetujui{" "}
                        <a href="#" className="text-red-500 hover:underline">Syarat & Ketentuan</a>
                        {" "}dan{" "}
                        <a href="#" className="text-red-500 hover:underline">Kebijakan Privasi</a>
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default RegisterPage;
